<template>
  <div v-if="fields.length > 0" class="mb-4 mt-4">
    <div v-for="field in fields" :key="field" class="mb-4">
      <!-- System Fields Display -->
      <div v-if="isSystemField(field)" class="mb-4">
        <div class="flex items-center gap-2 mb-2">
          <Label :for="field" class="text-gray-500">{{ field }}</Label>
          <span class="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">Auto-filled</span>
        </div>
        <div class="bg-gray-50 border border-gray-200 rounded-md p-3 text-gray-500 text-sm">
          This field is automatically managed by the system
        </div>
      </div>
      
      <!-- User Editable Fields -->
      <div v-else>
        <Label :for="field">{{ field }}</Label>
        <Textarea
          :id="field"
          v-model="localModelValue[field]"
          @input="handleFieldUpdate(field, $event)"
          :placeholder="`Enter ${field}`"
          rows="4"
          class="w-full"
          :disabled="isLoading"
          :aria-label="field"
        />
        <p v-if="fieldErrors[field]" class="text-red-500 text-sm mt-1">
          {{ fieldErrors[field] }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/toast/use-toast'

// Interface definitions
interface FieldErrors {
  [key: string]: string
}

// Properly typed props with defaults
const props = defineProps({
  fields: { type: Array as () => string[], required: true, default: () => [] },
  modelValue: { 
    type: Object as () => Record<string, string>, 
    required: true,
    default: () => ({})
  },
  isLoading: { type: Boolean, required: true, default: false }
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, string>): void
}>()

const { toast } = useToast()
const localModelValue = ref<Record<string, string>>({...props.modelValue})
const fieldErrors = ref<FieldErrors>({})

// Helper functions
const isSystemField = (field: string): boolean => {
  return field === 'email_thread' || field === 'skyfire_openapi'
}

const validateField = (field: string, value: string): string | null => {
  if (!value.trim() && !isSystemField(field)) {
    return `${field} is required`
  }
  return null
}

// Handle field updates
const handleFieldUpdate = (field: string, event: Event) => {
  const value = (event.target as HTMLTextAreaElement).value
  
  // Update local value
  localModelValue.value = {
    ...localModelValue.value,
    [field]: value
  }
  
  // Validate field
  const error = validateField(field, value)
  if (error) {
    fieldErrors.value[field] = error
  } else {
    delete fieldErrors.value[field]
  }
  
  // Emit update
  emit('update:modelValue', localModelValue.value)
}

// Update local model when props change
watch(() => props.modelValue, (newValue) => {
  localModelValue.value = {...newValue}
}, { deep: true, immediate: true })

// Watch fields for changes
watch(() => props.fields, (newFields) => {
  // Clean up any removed fields from local model
  const newLocalModel = {...localModelValue.value}
  Object.keys(newLocalModel).forEach(key => {
    if (!newFields.includes(key)) {
      delete newLocalModel[key]
    }
  })
  // Initialize new fields
  newFields.forEach(field => {
    if (!newLocalModel[field]) {
      newLocalModel[field] = ''
    }
  })
  localModelValue.value = newLocalModel
}, { immediate: true })

// Initialize component
onMounted(() => {
  // Initialize local model with all fields
  const initialModel = {...props.modelValue}
  props.fields.forEach(field => {
    if (!initialModel[field]) {
      initialModel[field] = ''
    }
  })
  localModelValue.value = initialModel
})
</script>

<style scoped>
.textarea {
  transition: all 0.2s ease;
}

.textarea:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background-color: rgba(0, 0, 0, 0.05);
}

.label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

.system-field {
  opacity: 0.7;
}

.error-message {
  color: rgb(239, 68, 68);
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>